/**
 * Sondas de las APIs de proveedores (Enlaces bancarios → tarjeta Bind / Banco CMF / Coelsa).
 * Mismo contrato para los tres, con el prefijo del proveedor:
 *
 *   GET  /<p>-probe/status          última corrida + catálogo de endpoints
 *   GET  /<p>-probe/history?hours=  6 | 12 | 24 | 168 → buckets por hora por endpoint
 *   POST /<p>-probe/run             corrida manual (autenticación + lecturas; nunca escrituras)
 */
import { authFetch, authHeader, jsonOrThrow, qsFrom } from "./monitoring";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

export type ProbeProvider = "bind" | "cmf" | "coelsa";

export type ProbeStatus =
  | "ok" | "auth" | "client_error" | "limited" | "not_enabled" | "provider_error" | "timeout" | "conn"
  | "skipped" | "pending" | "no_traffic" | "unused";

export interface ProbeResult {
  key: string;
  status: ProbeStatus;
  http: number | null;
  ms: number;
  note: string;
  /** "trafico": no lo sondeamos; es la última llamada real (Monitoreo › Proveedores). "ninguna": nadie lo llama. */
  source?: "trafico" | "ninguna";
  /** inicio (UTC) de la ventana de 5 min de la última llamada real */
  ts?: string;
}

export interface ProbeSummary {
  probed: number;
  ok: number;
  down: string[];
  auth: string[];
  responds_all: boolean;
  state: "ok" | "auth" | "down" | "unknown";
}

export interface ProbeRun {
  ts: string;
  trigger: "schedule" | "manual";
  operator: string;
  enabled?: boolean;
  error: string | null;
  probes: ProbeResult[];
  summary: ProbeSummary;
}

export interface CatalogEntry {
  key: string;
  n: number;
  method: string;
  path: string;
  label: string;
  impl: boolean;
  /** para los no sondeados: qué servicio nuestro hace la llamada real */
  via?: string | null;
  unused?: boolean;
}

export interface ProbeStatusResponse {
  provider: ProbeProvider;
  label: string;
  enabled: boolean;
  /** con qué identidad sondea ("cuenta 20-1-…", "CUIT …, cuenta …") */
  subject: string;
  config: Record<string, unknown>;
  every_minutes: number;
  catalog: CatalogEntry[];
  /** prefijo largo que la tabla abrevia con "…" (Bind) */
  path_abbrev: string;
  last: ProbeRun | null;
  pending: string[];
  pending_note: string;
  disabled_note: string | null;
}

export interface HourBucket {
  h: string;
  n: number;
  uptime: number | null;
  ok: number;
  p95: number | null;
}

export interface EndpointHistory {
  key: string;
  label: string;
  n: number;
  method: string;
  uptime: number | null;
  runs: number;
  p95: number | null;
  buckets: HourBucket[];
}

export interface ProbeHistory {
  hours: number;
  from: string;
  to: string;
  runs: number;
  endpoints: EndpointHistory[];
}

export const PROBE_HOURS = [6, 12, 24, 168] as const;

export async function getProbeStatus(p: ProbeProvider): Promise<ProbeStatusResponse> {
  const res = await authFetch(`${API_BASE}/${p}-probe/status`, { headers: authHeader() });
  return jsonOrThrow(res, `getProbeStatus(${p})`);
}

export async function getProbeHistory(p: ProbeProvider, hours: number): Promise<ProbeHistory> {
  const res = await authFetch(`${API_BASE}/${p}-probe/history${qsFrom({ hours: String(hours) })}`, { headers: authHeader() });
  return jsonOrThrow(res, `getProbeHistory(${p})`);
}

export async function runProbe(p: ProbeProvider): Promise<ProbeRun> {
  const res = await authFetch(`${API_BASE}/${p}-probe/run`, { method: "POST", headers: authHeader() });
  return jsonOrThrow(res, `runProbe(${p})`);
}

/** Texto corto por estado de sonda. */
export const PROBE_TEXT: Record<ProbeStatus, string> = {
  ok: "OK",
  auth: "rechaza credenciales",
  client_error: "responde (pedido inválido)",
  limited: "responde (límite de consultas)",
  not_enabled: "no habilitado para nosotros",
  provider_error: "error del proveedor",
  timeout: "sin respuesta",
  conn: "sin conexión",
  skipped: "sin dato de entrada",
  pending: "pendiente",
  no_traffic: "sin llamadas reales",
  unused: "sin uso (nadie lo llama)",
};

/** ¿La API respondió? (aunque nuestro pedido no haya sido perfecto) */
export function probeResponds(s: ProbeStatus): boolean {
  return s === "ok" || s === "auth" || s === "client_error" || s === "limited" || s === "not_enabled";
}
